import React from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useFormikContext, useField } from "formik";
import { Box, Typography } from "@mui/material";
import { environment } from "@/environment/environment";
import type { IAddress } from "@/services/model/address";
import GoogleAddressInner from "./GoogleAddressInner";
import DigiMapInner from "../DigiMap/DigiMapInner";

type AddressFieldProps = {
  id?: string;
  name: string;
  label?: string;
  required?: boolean;
  enableMap?: boolean;
  latLabelKey?: string;
  lngLabelKey?: string;
  autoFillFromGeo?: boolean;
  enableGoogleMaps?: boolean;
  token: string;
  GetAddressList: (token: string) => Promise<unknown>;
  showDialog?: (title: string, message: string, success?: boolean) => void;
};

const persist = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore persistence failures in private mode/quota edge cases.
  }
};

const JHB = { lat: -26.2041, lng: 28.0473 };

export default function AddressField(props: AddressFieldProps) {
  const {
    id,
    name,
    label,
    enableMap = true,
    latLabelKey,
    lngLabelKey,
    required,
    autoFillFromGeo = false,
    token,
    GetAddressList,
    showDialog,
  } = props;

  const [field] = useField<string>(name);
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const latField = `${name}__latitude`;
  const lngField = `${name}__longitude`;

  const toNumberOrUndefined = React.useCallback((n: unknown): number | undefined => {
    if (typeof n === "number" && Number.isFinite(n)) return n;
    if (typeof n === "string" && n.trim().length > 0) {
      const parsed = Number(n);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }, []);

  const initialAddress: IAddress = React.useMemo(() => {
    return {
      googleAddress: field.value ?? "",
      latitude: toNumberOrUndefined(values?.[latField]),
      longitude: toNumberOrUndefined(values?.[lngField]),
    };
  }, [field.value, values, latField, lngField, toNumberOrUndefined]);

  const [addr, setAddr] = React.useState<IAddress>(initialAddress);
  const addrRef = React.useRef<IAddress>(initialAddress);

  React.useEffect(() => {
    const next = field.value ?? "";
    setAddr((prev) => {
      if ((prev.googleAddress ?? "") === next) return prev;
      return { ...prev, googleAddress: next };
    });
  }, [field.value]);

  React.useEffect(() => {
    const lat = toNumberOrUndefined(values?.[latField]);
    const lng = toNumberOrUndefined(values?.[lngField]);
    setAddr((prev) => {
      if (prev.latitude === lat && prev.longitude === lng) return prev;
      return {
        ...prev,
        latitude: lat,
        longitude: lng,
      };
    });
  }, [values, latField, lngField, toNumberOrUndefined]);

  React.useEffect(() => {
    addrRef.current = addr;
  }, [addr]);

  const persistAll = React.useCallback(
    (a: IAddress) => {
      if (label) {
        persist(label, {
          googleAddress: a.googleAddress ?? "",
          latitude: a.latitude,
          longitude: a.longitude,
        });
      }
      if (latLabelKey && a.latitude != null) persist(latLabelKey, a.latitude);
      if (lngLabelKey && a.longitude != null) persist(lngLabelKey, a.longitude);
    },
    [label, latLabelKey, lngLabelKey],
  );

  const syncFormikAddress = React.useCallback(
    (next: IAddress) => {
      setFieldValue(name, next?.googleAddress ?? "", false);
      setFieldValue(latField, next?.latitude ?? "", false);
      setFieldValue(lngField, next?.longitude ?? "", false);
    },
    [latField, lngField, name, setFieldValue],
  );

  const setAddrAndPersist = React.useCallback(
    (updater: React.SetStateAction<IAddress>) => {
      const next =
        typeof updater === "function"
          ? (updater as (value: IAddress) => IAddress)(addrRef.current)
          : updater;

      addrRef.current = next;
      setAddr(next);
      syncFormikAddress(next);
    },
    [syncFormikAddress],
  );

  React.useEffect(() => {
    if (!autoFillFromGeo) return;

    const alreadyHasText = (addr.googleAddress ?? "").trim().length > 0;
    const alreadyHasPoint = addr.latitude != null && addr.longitude != null;
    if (alreadyHasText || alreadyHasPoint) return;

    const next = {
      googleAddress: "",
      latitude: JHB.lat,
      longitude: JHB.lng,
    };
    setAddrAndPersist(next);
    persistAll(next);
  }, [autoFillFromGeo, addr.googleAddress, addr.latitude, addr.longitude, persistAll, setAddrAndPersist]);

  const googleApiKey = String(environment.GoogleApiKey ?? "");
  const useDirectGoogleMaps =
    !!props.enableGoogleMaps && googleApiKey.trim().length > 0;

  return (
    <Box>
      {props.label && (
        <Typography
          variant="body2"
          sx={{ mb: 0.5 }}
          component="label"
          htmlFor={id || name}
        >
          {props.label}
          {required && (
            <span style={{ color: "#d32f2f", marginLeft: 4 }}>*</span>
          )}
        </Typography>
      )}
      {useDirectGoogleMaps ? (
        <APIProvider apiKey={googleApiKey} libraries={["places"]}>
          <GoogleAddressInner
            id={id}
            name={name}
            addressFields={addr}
            required={required}
            setAddressFields={setAddrAndPersist}
            enableMap={enableMap}
            token={token}
            GetAddressList={GetAddressList}
            showDialog={showDialog}
          />
        </APIProvider>
      ) : (
        <DigiMapInner
          addressFields={addr}
          setAddressFields={setAddrAndPersist}
          enableMap={enableMap}
          token={token}
          GetAddressList={GetAddressList}
          showDialog={showDialog}
        />
      )}
    </Box>
  );
}
